import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import SpeckledGradient from "./SpeckledGradient";
import ThemeSelector from "./ThemeSelector";
import lightLogo from "../assets/logos/T3-logo-lightMode.svg";
import darkLogo from "../assets/logos/T3-logo-darkMode.svg";

const taglines = {
  Time: "Spend your time on your mission.",
  Talent: "Decades of nonprofit expertise, built in.",
  Treasure: "Clarity on every dollar, in and out.",
};

const AuthLayout = ({ children, animate = true }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [hoveredWord, setHoveredWord] = useState(null);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        width: "100vw",
        overflow: "hidden",
        backgroundColor: isDark ? theme.palette.background.default : "#fff",
      }}>
      {/* Speckled gradient canvas behind everything */}
      <SpeckledGradient variant={isDark ? "dark" : "light"} animate={animate} />

      {/* Main grid layout */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
          minHeight: "100vh",
        }}>
        {/* Left panel — branding, hugs the center (bottom on mobile) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-end" },
            justifyContent: { xs: "flex-start", md: "center" },
            order: { xs: 3, md: 0 },
            pt: { xs: 1, md: 6 },
            pb: { xs: 4, md: 6 },
            px: { xs: 4, md: 0 },
            pr: { md: 6 },
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}>
            <Box
              component="img"
              src={isDark ? darkLogo : lightLogo}
              alt="T3 Books"
              sx={{
                width: { xs: 180, md: 220 },
                height: "auto",
                mb: 2.5,
              }}
            />
            <Box
              component="p"
              sx={{
                fontFamily: "MontserratMed",
                fontSize: { xs: "1.1rem", md: "1.3rem" },
                color: theme.palette.text.secondary,
                textAlign: "center",
                m: 0,
                maxWidth: 320,
                lineHeight: 1.5,
              }}
              onMouseLeave={() => setHoveredWord(null)}>
              {["Time", "Talent", "Treasure"].map((word, i) => (
                <React.Fragment key={word}>
                  {i > 0 && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: isDark
                          ? theme.palette.primary.main
                          : theme.palette.secondary.main,
                        mx: 1.5,
                        verticalAlign: "middle",
                        transition: "opacity 250ms ease-out",
                        opacity: hoveredWord && hoveredWord !== word ? 0.35 : 1,
                      }}
                    />
                  )}
                  <Box
                    component="span"
                    onMouseEnter={() => setHoveredWord(word)}
                    sx={{
                      cursor: "default",
                      transition:
                        "color 250ms ease-out, transform 250ms ease-out, opacity 250ms ease-out",
                      display: "inline-block",
                      px: 0.5,
                      transform:
                        hoveredWord === word ? "scale(1.1)" : "scale(1)",
                      color:
                        hoveredWord === word
                          ? isDark
                            ? theme.palette.primary.main
                            : theme.palette.secondary.main
                          : "inherit",
                      opacity:
                        hoveredWord && hoveredWord !== word ? 0.35 : 1,
                    }}>
                    {word}
                  </Box>
                </React.Fragment>
              ))}
            </Box>

            {/* Tagline container — fixed height to prevent layout shift */}
            <Box
              sx={{
                height: 32,
                mt: 1,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
              }}>
              <Box
                component="span"
                key={hoveredWord}
                sx={{
                  fontFamily: "MontserratMed",
                  fontSize: { xs: "0.82rem", md: "0.9rem" },
                  color: theme.palette.text.secondary,
                  opacity: hoveredWord ? 1 : 0,
                  transform: hoveredWord
                    ? "translateY(0)"
                    : "translateY(6px)",
                  transition:
                    "opacity 500ms cubic-bezier(0.1, 0, 0.2, 1), transform 350ms ease-out",
                  textAlign: "center",
                  letterSpacing: "0.01em",
                }}>
                {hoveredWord ? taglines[hoveredWord] : ""}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Center accent line — stylistic, not full-height */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            py: 6,
          }}>
          <Box
            sx={{
              width: "3px",
              height: "40%",
              minHeight: 180,
              maxHeight: 320,
              backgroundColor: isDark
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
              borderRadius: "1px",
            }}
          />
        </Box>

        {/* Mobile horizontal accent line (between form and branding) */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            justifyContent: "center",
            order: { xs: 2 },
            pt: 1,
            pb: 0,
          }}>
          <Box
            sx={{
              height: "3px",
              width: "30%",
              minWidth: 120,
              maxWidth: 200,
              backgroundColor: isDark
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
              borderRadius: "1px",
            }}
          />
        </Box>

        {/* Right panel — form, hugs the center (top on mobile) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: "center",
            order: { xs: 1, md: 0 },
            py: { xs: 3, md: 6 },
            px: { xs: 3, md: 0 },
            pt: { xs: 6 },
            pl: { md: 6 },
          }}>
          {children}
        </Box>
      </Box>

      <ThemeSelector />
    </Box>
  );
};

export default AuthLayout;
